import {
	multicastEndpointKey,
	normalizeMulticastEndpoint,
	toWindowMulticastSubscribeConfig,
} from "./endpoint-config.js";
import { subscriptionDeliversPacketBatches } from "./packet-batch.js";
import type {
	TransportConfig,
	WindowMulticastGateway,
	WindowMulticastSubscription,
	WindowMulticastSubscriptionState,
} from "./types.js";

export interface MulticastSubscriptionConsumer {
	onBytes(data: ArrayBuffer): void;
	onState?(state: WindowMulticastSubscriptionState): void;
	onError?(error: Error): void;
}

interface PoolEntry {
	subscription: WindowMulticastSubscription;
	consumers: Map<symbol, MulticastSubscriptionConsumer>;
	/**
	 * Exactly one packet surface is listened to: `packets` when the provider
	 * advertises it, `bytes` otherwise (BLO-36599).
	 */
	packetListener:
		| { type: "bytes"; listener: (event: CustomEvent<ArrayBuffer>) => void }
		| { type: "packets"; listener: (event: CustomEvent<readonly ArrayBuffer[]>) => void };
	stateListener: (event: CustomEvent<WindowMulticastSubscriptionState>) => void;
	errorListener: (event: CustomEvent<unknown>) => void;
	closed: boolean;
}

interface PoolRecord { claims: number; entry: Promise<PoolEntry>; }

export interface MulticastSubscriptionLease {
	readonly key: string;
	release(): Promise<void>;
}

/** Shares only IWA multicast/AMT subscriptions. Unicast sessions remain per manager. */
export class MulticastSubscriptionPool {
	readonly #entries = new Map<string, PoolRecord>();
	readonly #gatewayIds = new WeakMap<object, number>();
	#nextGatewayId = 1;

	async acquire(
		gateway: WindowMulticastGateway,
		config: TransportConfig,
		consumer: MulticastSubscriptionConsumer,
	): Promise<MulticastSubscriptionLease> {
		const endpoint = normalizeMulticastEndpoint({
			groupAddress: config.group,
			sourceAddress: config.source,
			port: config.port,
			mode: config.mode,
			amtRelay: config.amtRelay,
			amtRelayPort: config.amtRelayPort,
			amtDiscovery: config.amtDiscovery,
		});
		if (!endpoint) throw new Error("Invalid multicast endpoint");
		if (typeof gateway.subscribe !== "function") throw new Error("window.multicast.subscribe unavailable");

		const key = multicastEndpointKey(endpoint);
		const internalKey = `${this.#gatewayId(gateway)}|${key}`;
		let record = this.#entries.get(internalKey);
		if (!record) {
			record = { claims: 0, entry: Promise.resolve(null as unknown as PoolEntry) };
			const createdRecord = record;
			record.entry = this.#createEntry(gateway, endpoint, (entry) => {
				if (this.#entries.get(internalKey) === createdRecord) this.#entries.delete(internalKey);
				this.#detach(entry);
			}).catch((error) => {
				if (this.#entries.get(internalKey) === createdRecord) this.#entries.delete(internalKey);
				throw error;
			});
			this.#entries.set(internalKey, record);
		}

		record.claims++;
		let entry: PoolEntry;
		try {
			entry = await record.entry;
		} catch (error) {
			record.claims--;
			throw error;
		}
		if (entry.closed) {
			record.claims--;
			return this.acquire(gateway, config, consumer);
		}
		const token = Symbol(key);
		entry.consumers.set(token, consumer);
		let released = false;
		return {
			key,
			release: async () => {
				if (released) return;
				released = true;
				entry.consumers.delete(token);
				record.claims--;
				if (record.claims > 0) return;
				if (this.#entries.get(internalKey) === record) this.#entries.delete(internalKey);
				this.#detach(entry);
				if (!entry.closed) await entry.subscription.unsubscribe();
			},
		};
	}

	get size(): number { return this.#entries.size; }

	async #createEntry(
		gateway: WindowMulticastGateway,
		endpoint: NonNullable<ReturnType<typeof normalizeMulticastEndpoint>>,
		onClosed: (entry: PoolEntry) => void,
	): Promise<PoolEntry> {
		const subscription = await gateway.subscribe!(toWindowMulticastSubscribeConfig({
			group: endpoint.groupAddress,
			...(endpoint.sourceAddress ? { source: endpoint.sourceAddress } : {}),
			port: endpoint.port,
			...(endpoint.mode ? { mode: endpoint.mode } : {}),
			...(endpoint.amtRelay ? { amtRelay: endpoint.amtRelay } : {}),
			...(endpoint.amtRelayPort ? { amtRelayPort: endpoint.amtRelayPort } : {}),
			...(endpoint.amtDiscovery ? { amtDiscovery: endpoint.amtDiscovery } : {}),
		}));
		const consumers = new Map<symbol, MulticastSubscriptionConsumer>();
		let entry: PoolEntry;
		// A batch is delivered as the same per-packet onBytes calls the bytes
		// surface makes, in arrival order and with each call isolated, so a
		// consumer cannot tell the two apart; only the DOM event per packet is
		// gone. The batch array and its elements are the provider's own, uncopied.
		const packetListener: PoolEntry["packetListener"] = subscriptionDeliversPacketBatches(subscription)
			? {
					type: "packets",
					listener: (event) => {
						for (const data of event.detail) {
							this.#dispatch(consumers, (consumer) => consumer.onBytes(data));
						}
					},
				}
			: {
					type: "bytes",
					listener: (event) => {
						this.#dispatch(consumers, (consumer) => consumer.onBytes(event.detail));
					},
				};
		const stateListener = (event: CustomEvent<WindowMulticastSubscriptionState>) => {
			this.#dispatch(consumers, (consumer) => consumer.onState?.(event.detail));
			if (event.detail === "closed" && !entry.closed) {
				entry.closed = true;
				onClosed(entry);
			}
		};
		const errorListener = (event: CustomEvent<unknown>) => {
			const error = event.detail instanceof Error ? event.detail : new Error(String(event.detail));
			this.#dispatch(consumers, (consumer) => consumer.onError?.(error));
		};
		entry = { subscription, consumers, packetListener, stateListener, errorListener, closed: false };
		if (packetListener.type === "packets") {
			subscription.addEventListener("packets", packetListener.listener);
		} else {
			subscription.addEventListener("bytes", packetListener.listener);
		}
		subscription.addEventListener("state", stateListener);
		subscription.addEventListener("error", errorListener);
		return entry;
	}

	#gatewayId(gateway: WindowMulticastGateway): number {
		const object = gateway as object;
		let id = this.#gatewayIds.get(object);
		if (!id) {
			id = this.#nextGatewayId++;
			this.#gatewayIds.set(object, id);
		}
		return id;
	}

	#detach(entry: PoolEntry): void {
		const { packetListener } = entry;
		if (packetListener.type === "packets") {
			entry.subscription.removeEventListener("packets", packetListener.listener);
		} else {
			entry.subscription.removeEventListener("bytes", packetListener.listener);
		}
		entry.subscription.removeEventListener("state", entry.stateListener);
		entry.subscription.removeEventListener("error", entry.errorListener);
	}

	#dispatch(
		consumers: Map<symbol, MulticastSubscriptionConsumer>,
		callback: (consumer: MulticastSubscriptionConsumer) => void,
	): void {
		for (const consumer of consumers.values()) {
			try { callback(consumer); } catch (error) { console.error("[Transport] multicast consumer callback failed", error); }
		}
	}
}

export const multicastSubscriptionPool = new MulticastSubscriptionPool();
